import { useLocations } from "@/hooks/use-location";
import { useFormContext, useWatch } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TIPOS_DOCUMENTO } from "@/config/form-options";

import { Trash2 } from "lucide-react";
import { Paises } from "@/enum/paises.enum";
import { Button } from "@/components/ui/button";
import { useState } from "react";

export function MiembroForm({
  index,
  stepId,
  remove,
  paises,
}: {
  index: number;
  stepId: string;
  miembro: any;
  remove: (index: number) => void;
  paises: { label: string; value: string }[];
}) {
  const { control } = useFormContext();

  const [paisId, setPaisId] = useState<string | undefined>();
  const [departamentoId, setDepartamentoId] = useState<string | undefined>();

  const pais = useWatch({
    control,
    name: `${stepId}.miembros.${index}.pais`,
  });

  const departamento = useWatch({
    control,
    name: `${stepId}.miembros.${index}.departamento`,
  });

  const { departamentos, ciudades } = useLocations(paisId, departamentoId);

  return (
    <div
      key={`miembro.${index}`}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 p-4 border rounded-2xl shadow-sm relative"
    >
      {/* Tipo de documento */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.tipoDocumento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de documento</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.filter((t) => t.value !== "NIT").map(
                    (doc) => (
                      <SelectItem key={doc.value} value={doc.value}>
                        {doc.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* No. Documento */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.numeroDocumento`}
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel>No. Documento</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Número de documento" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Nombre completo */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.nombreCompleto`}
        render={({ field }) => (
          <FormItem className="md:col-span-3 lg:col-span-2">
            <FormLabel>Nombres completos / Razón social</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Nombre completo o Razón social" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Teléfono */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.telefono`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Teléfono</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Teléfono" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* País */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.pais`}
        render={({ field }) => (
          <FormItem className="w-full min-w-0">
            <FormLabel>País</FormLabel>
            <FormControl>
              <Select
                onValueChange={(label) => {
                  const selected = paises.find((p) => p.label === label);
                  if (selected) {
                    setPaisId(selected.value); // este es el que viaja a tu API
                  }

                  // Si quieres que el form guarde el id real
                  field.onChange(label ?? "");
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un país" />
                </SelectTrigger>
                <SelectContent>
                  {paises.map((p) => (
                    <SelectItem key={p.value} value={p.label}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Departamento */}
      {pais === Paises.Colombia && (
        <FormField
          control={control}
          name={`${stepId}.miembros.${index}.departamento`}
          render={({ field }) => (
            <FormItem className="w-full min-w-0">
              <FormLabel>Departamento</FormLabel>
              <FormControl>
                <Select
                  onValueChange={(label) => {
                    const selected = departamentos.find(
                      (d) => d.label === label
                    );
                    if (selected) {
                      setDepartamentoId(selected.value); // este es el que viaja a tu API
                    }

                    // Si quieres que el form guarde el id real
                    field.onChange(label ?? "");
                  }}
                  value={field.value}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona un departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.value} value={d.label}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Ciudad */}
      <FormField
        control={control}
        name={`${stepId}.miembros.${index}.ciudad`}
        render={({ field }) => (
          <FormItem className="w-full min-w-0">
            <FormLabel>Ciudad</FormLabel>
            <FormControl>
              {pais === Paises.Colombia && departamento ? (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona una ciudad" />
                  </SelectTrigger>
                  <SelectContent>
                    {(ciudades ?? []).map((c) => (
                      <SelectItem key={c.value} value={c.label}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...field} placeholder="Ingresa la ciudad" />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Botón eliminar */}
      <div className="absolute top-2 right-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => remove(index)}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
